import {
  controlFilter,
  intersectFilters,
  isSourceBoundTo,
  type ControlFilter,
  type FilterListValues,
} from './controls.js';
import {FilterType} from '../constants.js';
import type {Filter, Filters} from '../types.js';
import type {Dataset, KeplerMapConfig} from './types.js';

function matchNothing(control: ControlFilter, remote: boolean): Filter {
  if (!remote) {
    return {in: {values: []}};
  }
  if ('time' in control) {
    return {time: {values: [[1, 0]]}};
  }
  const values = (
    'in' in control ? control.in.values : control.not_in.values
  ) as FilterListValues;
  return {in: {values}, [FilterType.NOT_IN]: {values}};
}

function withTimeOffset(filter: Filter): Filter {
  const time = filter.time!;
  return {
    ...filter,
    time: {...time, params: {offsetBy: time.values[0][0]}},
  };
}

export function composeDatasetFilters(
  config: Pick<KeplerMapConfig, 'filters' | 'sqlParameters'> | undefined,
  dataset: Pick<Dataset, 'id' | 'type'>,
  remote: boolean
): Filters | undefined {
  const base: Filters | undefined = config?.filters?.[dataset.id];
  const parameters = config?.sqlParameters;
  if (dataset.type === 'raster' || !Array.isArray(parameters)) {
    return base;
  }
  const entries = Object.entries(base ?? {}).map(([column, filter]) => ({
    column,
    filter,
  }));
  let applied = false;
  for (const parameter of parameters) {
    if (!isSourceBoundTo(parameter, dataset)) {
      continue;
    }
    const {column} = parameter.binding;
    const control = controlFilter(parameter, parameter.selection);
    if (!control) {
      continue;
    }
    applied = true;
    const entry = entries.find((candidate) => candidate.column === column);
    const result = intersectFilters(entry?.filter, control);
    const filter = result.empty
      ? matchNothing(control, remote)
      : !remote && 'time' in control
        ? withTimeOffset(result.filter)
        : result.filter;
    if (entry) {
      entry.filter = filter;
    } else {
      entries.push({column, filter});
    }
  }
  return applied
    ? Object.fromEntries(entries.map(({column, filter}) => [column, filter]))
    : base;
}
